import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Eraser, ListOrdered } from 'lucide-react';
import { Botao, Selo } from '../../componentes/base';
import { Carregando, Erro } from '../../componentes/estados';
import { AreaSensivel, CampoControlado } from '../../componentes/formulario';
import { Modal } from '../../componentes/sobreposicao';
import { TextoCortado } from '../../componentes/texto-cortado';
import { useToast } from '../../componentes/toast';
import { useValidacaoLocal } from '../../componentes/validacao-local';
import { anunciarOcultos } from '../../componentes/sensivel';
import { get, mensagemDeErro, post } from '../../nucleo/api';
import { data as dataBr, dinheiro, nomeParceiro, simboloMoeda } from '../../nucleo/formato';
import { exemploDinheiroEm } from '../../nucleo/mascaras';
import { distribuirCredito, hojeLocal, invalidarContas, type Documento, type FaturasAplicaveis } from './tipos';

/**
 * Fase 2, W34: "Aplicar em fatura" da nota de crédito (`POST /contas/documentos/:id/aplicar-credito`, permissão
 * `contas.baixar`). Lista as faturas em aberto do mesmo cliente, da mesma empresa e da mesma moeda (`faturas-aplicaveis`),
 * com o crédito já repartido pela que vence primeiro; cada valor é editável (vazio = fora). O total não passa do saldo da
 * nota e o valor de cada fatura não passa do saldo dela (a tela avisa antes; o servidor confere com a trava). Sem
 * dinheiro: nenhum lançamento no Financeiro. A chave de idempotência nasce com o modal.
 */
export function AplicarCredito({ nota, aoFechar }: { nota: Pick<Documento, 'id' | 'numero' | 'saldo_centavos' | 'moeda'> | null; aoFechar: () => void }) {
  if (!nota) return null;
  return <Corpo nota={nota} aoFechar={aoFechar} />;
}

function Corpo({ nota, aoFechar }: { nota: Pick<Documento, 'id' | 'numero' | 'saldo_centavos' | 'moeda'>; aoFechar: () => void }) {
  const { t } = useTranslation('contas');
  const { t: tn } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const [chave] = useState(() => crypto.randomUUID());
  const [data, setData] = useState(hojeLocal());
  const [observacao, setObservacao] = useState('');
  const [valores, setValores] = useState<Record<string, number | null> | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  useValidacaoLocal(null, erro);
  const q = useQuery({ queryKey: ['/contas/documentos', 'faturas-aplicaveis', nota.id], queryFn: () => get<FaturasAplicaveis>(`/contas/documentos/${nota.id}/faturas-aplicaveis`) });
  const saldoNota = q.data?.nota.saldo_centavos ?? nota.saldo_centavos;
  const faturas = useMemo(() => q.data?.itens ?? [], [q.data]);
  // Antes de mexer: o crédito repartido pela fatura que vence primeiro.
  const padrao = useMemo(() => (q.data ? distribuirCredito(q.data.nota.saldo_centavos, q.data.itens) : {}), [q.data]);
  const atual: Record<string, number | null> = valores ?? padrao;
  const escolhidas = faturas.filter((f) => (atual[f.id] ?? 0) > 0);
  const total = escolhidas.reduce((a, f) => a + (atual[f.id] ?? 0), 0);
  const acimaDaFatura = (id: string, saldo: number) => (atual[id] ?? 0) > saldo;
  const algumAcima = faturas.some((f) => acimaDaFatura(f.id, f.saldo_centavos));
  const acimaDaNota = total > saldoNota;
  const valido = escolhidas.length > 0 && !algumAcima && !acimaDaNota && /^\d{4}-\d{2}-\d{2}$/.test(data);
  const moeda = nota.moeda;

  const aplicar = useMutation({
    mutationFn: () =>
      post<{ total_centavos: number; aplicacoes: unknown[] }>(
        `/contas/documentos/${nota.id}/aplicar-credito`,
        { data, observacao: observacao.trim() || undefined, aplicacoes: escolhidas.map((f) => ({ fatura_id: f.id, valor_centavos: atual[f.id] })) },
        { 'Idempotency-Key': chave },
      ),
    onSuccess: async (r) => {
      await invalidarContas(qc);
      toast.sucesso(t('w34.aplicado', { count: r.aplicacoes.length, valor: dinheiro(r.total_centavos, moeda) }));
      aoFechar();
    },
    onError: (e) => setErro(mensagemDeErro(e)),
  });

  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      largura="lg"
      titulo={t('w34.titulo', { numero: nota.numero })}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" disabled={!valido} carregando={aplicar.isPending} onClick={() => (setErro(null), aplicar.mutate())}>
            {t('w34.confirmar')}
          </Botao>
        </>
      }
    >
      <div className="flex min-w-0 flex-col gap-4" data-aplicar-credito>
        <p className="text-sm text-tinta-2">{anunciarOcultos(t('w34.texto', { saldo: dinheiro(saldoNota, moeda), cliente: nomeParceiro(q.data?.nota.parceiro_nome ?? null) }))}</p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <CampoControlado def={{ nome: 'data_aplicacao', rotulo: t('w34.data'), tipo: 'data', obrigatorio: true, ajuda: t('w34.dataAjuda') }} valor={data} aoMudar={(v) => setData(String(v ?? ''))} />
          <div className="flex flex-wrap items-end gap-2">
            <Botao tamanho="sm" icone={<ListOrdered className="size-4" />} disabled={!faturas.length} onClick={() => setValores(distribuirCredito(saldoNota, faturas))}>
              {t('w34.distribuir')}
            </Botao>
            <Botao tamanho="sm" variante="fantasma" icone={<Eraser className="size-4" />} disabled={!escolhidas.length} onClick={() => setValores({})}>
              {t('w34.limpar')}
            </Botao>
          </div>
        </div>

        {q.isPending && <Carregando />}
        {q.isError && <Erro erro={q.error} aoTentar={() => q.refetch()} />}
        {q.data && !faturas.length && <p className="rounded-lg border border-borda px-3 py-2 text-sm text-tinta-2" data-sem-faturas>{t('w34.semFaturas')}</p>}
        {faturas.length > 0 && (
          <ul className="flex max-h-80 flex-col divide-y divide-borda overflow-y-auto rounded-lg border border-borda" tabIndex={0} role="region" aria-label={t('w34.faturas')} data-faturas-aplicaveis>
            {faturas.map((f) => (
              <li key={f.id} className="flex min-w-0 flex-wrap items-end justify-between gap-x-4 gap-y-2 px-3 py-2 text-sm" data-fatura={f.numero}>
                <span className="flex min-w-0 flex-1 basis-48 flex-col">
                  <span className="flex min-w-0 flex-wrap items-center gap-2">
                    <TextoCortado className="numero min-w-0 font-medium">{f.numero}</TextoCortado>
                    {f.atrasado && <Selo cor="laranja">{t('estado.atrasadoDias', { count: f.dias_atraso })}</Selo>}
                  </span>
                  <span className="numero text-xs text-tinta-3">{anunciarOcultos(t('w34.vencimentoSaldo', { vencimento: dataBr(f.vencimento), saldo: dinheiro(f.saldo_centavos, f.moeda) }))}</span>
                </span>
                <div className="w-full sm:w-44">
                  <CampoControlado
                    def={{ nome: `valor_${f.id}`, rotulo: t('w34.valorNaFatura', { numero: f.numero }), tipo: 'dinheiro', placeholder: exemploDinheiroEm(simboloMoeda(f.moeda)) }}
                    valor={atual[f.id] ?? null}
                    aoMudar={(v) => setValores((m) => ({ ...(m ?? padrao), [f.id]: (v as number | null) ?? null }))}
                    erro={acimaDaFatura(f.id, f.saldo_centavos) ? t('w34.acimaDaFatura', { saldo: dinheiro(f.saldo_centavos, f.moeda) }) : undefined}
                  />
                </div>
              </li>
            ))}
          </ul>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="flex min-w-0 flex-col rounded-lg border border-borda bg-sup-2 px-3 py-2">
            <span className="kicker">{t('w34.total')}</span>
            <TextoCortado className="numero text-xl font-semibold text-tinta-1">{dinheiro(total, moeda)}</TextoCortado>
          </div>
          <div className="flex min-w-0 flex-col rounded-lg border border-borda bg-sup-2 px-3 py-2">
            <span className="kicker">{t('w34.sobra')}</span>
            <TextoCortado className={`numero text-xl font-semibold ${acimaDaNota ? 'text-perigo' : 'text-tinta-2'}`}>{dinheiro(Math.max(saldoNota - total, 0), moeda)}</TextoCortado>
          </div>
        </div>
        {acimaDaNota && (
          <p role="alert" className="rounded-lg border border-perigo px-3 py-2 text-sm text-perigo">
            {t('w34.acimaDaNota', { saldo: dinheiro(saldoNota, moeda) })}
          </p>
        )}
        <p className="text-xs text-tinta-3">{t('w34.semCaixa')}</p>
        <label className="flex flex-col gap-1.5 text-sm font-medium text-tinta-2">
          {t('w34.observacao')}
          <AreaSensivel rows={2} maxLength={300} value={observacao} onChange={(e) => setObservacao(e.target.value)} className="w-full rounded-lg border border-borda-campo bg-campo px-3 py-2 text-sm text-tinta-1 focus:border-foco focus:outline-2 focus:outline-offset-0 focus:outline-foco" />
        </label>
        {erro && (
          <p role="alert" className="rounded-lg border border-perigo px-3 py-2 text-sm text-perigo">
            {erro}
          </p>
        )}
      </div>
    </Modal>
  );
}
